import { forwardRef } from 'react'
import { Separator } from '@/components/ui/separator'
import type { RemapExperienceForViewOnly } from '@/features/experience-view-only/types'
import { useTranslation } from '@/translations/i18n'
import { AnswerField, Content, YesNoAnswerField } from './atoms'

export const PricingDiscountStep = forwardRef<
	HTMLDivElement,
	{ experience: RemapExperienceForViewOnly }
>(({ experience }, ref) => {
	const { t } = useTranslation()

	return (
		<div className='flex w-full flex-col' ref={ref}>
			<Content
				margin={6}
				title={t('experience_shell:step_pricing_discount')}
			>
				<AnswerField
					hasValue={
						!!experience.expertise.answers.guest_individual_price
							?.answer
					}
					labelTitle={t(
						'vetting:criteria.experience.price_per_person'
					)}
					value={
						experience.expertise.answers.guest_individual_price
							?.answer
					}
				/>
				<YesNoAnswerField
					labelTitle={t('experience_view_only:allow_private_groups')}
					yesNoValue={
						experience.expertise.answers.instance_private_groups
							?.answer
					}
				/>

				{!!experience.expertise.answers.instance_private_groups
					?.answer && (
					<AnswerField
						hasValue={
							!!experience.expertise.answers.group_minimum_price
								?.answer
						}
						labelTitle={t('experience_shell:minimum_price')}
						value={
							experience.expertise.answers.group_minimum_price
								?.answer
						}
					/>
				)}

				<AnswerField
					hasValue={
						!!experience.expertise.answers.maximum_public_group_size
							?.answer
					}
					labelTitle={t('experience_shell:maximum_public_group_size')}
					value={
						experience.expertise.answers.maximum_public_group_size
							?.answer
					}
				/>
			</Content>

			<Separator orientation='horizontal' />

			<Content margin={6}>
				<YesNoAnswerField
					labelTitle={t('experience_shell:limited_time')}
					yesNoValue={!!experience.limitedTimeDiscount}
				>
					{experience.limitedTimeDiscount
						? `${experience.limitedTimeDiscount}%`
						: null}
				</YesNoAnswerField>
				<AnswerField
					hasValue={!!experience.discounts.length}
					labelTitle={t('experience_shell:large_group_discount')}
				>
					{experience.discounts.map((discount) => {
						const discountText = t(
							'experience_view_only:groups_discounts',
							{
								guests: discount.groupSize,
								percent: discount.discountInPercentage
							}
						)
						return (
							<li
								key={`${discount.groupSize}-${discount.discountInPercentage}`}
								title={discountText}
							>
								{discountText}
							</li>
						)
					})}
				</AnswerField>
				<YesNoAnswerField
					labelTitle={t('experience_shell:early_bird_discount')}
					yesNoValue={
						experience.expertise.answers.early_bird_discount?.answer
					}
				/>
			</Content>
		</div>
	)
})

PricingDiscountStep.displayName = 'PricingDiscountStep'
